import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DeleteBulkResponse } from '@core/interfaces/bulk-operations';
import { DEFAULT_CONNECTION_PROFILE_NAME } from '@core/hubconfig';
import { HubHttpPaginationResponse } from '@core/interfaces/HubResponse';
import { BulkNamesResponse } from '@core/interfaces/common';
import {
  Connection,
  ConnectionProfileParams,
  ConnectionProfiles,
  ConnectorConfig,
  ConnectorOptionsProperty,
  ConnectorOptionsPropertyType,
  ConnectorOptionsWrapper,
} from '@core/interfaces/connection-profile';
import { splitLabelFilter } from '@core/interfaces/label-filter';
import { TestConnectionResult } from '@core/interfaces/module';
import { Pagination } from '@core/interfaces/pagination';
import { environment } from '@env';
import { PermissionsService } from '@core/services/permissions/permissions.service';
import { isNotNullOrUndefined } from '@shared/utils/null-check';
import { Observable, pipe, UnaryFunction } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ConnectionProfilesService {
  private http = inject(HttpClient);
  private readonly permissionsService = inject(PermissionsService);

  private readonly entryPoint = `${environment.BACKEND_DOMAIN}/api/v1/modules`;

  private readonly connectionEntryPoint = `${environment.BACKEND_DOMAIN}/api/v1/connections`;

  all(query?: {
    sort?: string;
    pagination?: Pagination;
    usage?: boolean;
    connector_name?: string;
    labels?: string[];
  }): Observable<HubHttpPaginationResponse<Connection>> {
    let params = new HttpParams();
    if (query?.pagination?.offset) {
      params = params.set('offset', query?.pagination?.offset);
    }
    if (query?.pagination?.limit) {
      params = params.set('limit', query?.pagination?.limit);
    }
    if (query?.sort) {
      params = params.set('sort', query?.sort);
    }
    if (isNotNullOrUndefined(query?.usage)) {
      params = params.set('usage', query?.usage as boolean);
    }
    if (query?.connector_name) {
      params = params.set('connector_name', query?.connector_name);
    }
    if (query?.labels && query.labels.length > 0) {
      const { labels, noneLabels } = splitLabelFilter(query.labels);
      if (labels.length > 0) {
        params = params.set('labels', labels.join(','));
      }
      params = params.set('noneLabels', noneLabels.toString());
    }
    return this.http.get<HubHttpPaginationResponse<Connection>>(`${this.connectionEntryPoint}`, {
      params,
    });
  }

  additionalInfo(
    connectionId: string,
  ): Observable<{ modules?: Record<string, unknown>[]; projects?: Record<string, unknown>[] }> {
    return this.http.get<{
      modules?: Record<string, unknown>[];
      projects?: Record<string, unknown>[];
    }>(`${this.connectionEntryPoint}/${connectionId}/flyout`);
  }

  one(connectionId: string): Observable<Connection> {
    return this.http
      .get<Connection>(`${this.connectionEntryPoint}/${connectionId}?usage=true`)
      .pipe(this.fillDefaultProfileValue());
  }

  createConnection(connection: {
    name: string;
    connector: string;
    profiles: ConnectionProfileParams;
    modifiedBy: string;
    modifiedAt: Date | number;
    createdBy: string;
    createdAt: Date | number;
  }): Observable<Connection> {
    return this.http.post<Connection>(`${this.connectionEntryPoint}`, connection).pipe(
      this.fillDefaultProfileValue(),
      tap(() => this.permissionsService.invalidateCurrentUserResourceRoles()),
    );
  }

  profiles(moduleId: string): Observable<ConnectionProfiles> {
    return this.http.get<ConnectionProfiles>(`${this.entryPoint}/${moduleId}/config`);
  }

  profilesByBulk(moduleIds: string[]): Observable<HubHttpPaginationResponse<ConnectionProfiles>> {
    return this.http.post<HubHttpPaginationResponse<ConnectionProfiles>>(
      `${this.entryPoint}/bulk/read-config`,
      { ids: moduleIds },
    );
  }

  updateConnection(connection: Connection): Observable<Connection> {
    return this.http
      .put<Connection>(`${this.connectionEntryPoint}/${connection.id}`, connection)
      .pipe(this.fillDefaultProfileValue());
  }

  deleteConnection(connectionId: string): Observable<void> {
    return this.http.delete<void>(`${this.connectionEntryPoint}/${connectionId}`);
  }

  destroyBulk(ids: string[]): Observable<DeleteBulkResponse> {
    return this.http.delete<DeleteBulkResponse>(`${this.connectionEntryPoint}/bulk/delete`, {
      body: { ids },
    });
  }

  update(
    moduleId: string,
    profiles: Record<string, ConnectionProfileParams>,
  ): Observable<Record<string, ConnectionProfileParams>> {
    return this.http.put<Record<string, ConnectionProfileParams>>(
      `${this.entryPoint}/${moduleId}/profiles`,
      profiles,
    );
  }

  options(connectorName: string): Observable<ConnectorOptionsWrapper> {
    return this.http.get<ConnectorOptionsWrapper>(
      `${this.connectionEntryPoint}/connector/${connectorName}/options`,
    );
  }

  getConnectionProperties(
    connectionName: string,
  ): Observable<Record<string, ConnectorOptionsProperty>> {
    return this.options(connectionName).pipe(this.convertOptionsToProperties());
  }

  convertOptionsToProperties(): UnaryFunction<
    Observable<ConnectorOptionsWrapper>,
    Observable<Record<string, ConnectorOptionsProperty>>
  > {
    return pipe(
      map(({ connectorOptions }) => {
        const connectionProperties: Record<string, ConnectorOptionsProperty> = {};
        connectorOptions.connectionProperties.properties.subGroups.forEach((subGroup) => {
          Object.assign(connectionProperties, subGroup.properties);
        });
        this.mergeRseFromFetchProperties(connectorOptions, connectionProperties);
        return connectionProperties;
      }),
    );
  }

  private mergeRseFromFetchProperties(
    connectorOptions: ConnectorConfig,
    connectionProperties: Record<string, ConnectorOptionsProperty>,
  ): void {
    const rseGroup = Object.values(connectorOptions.fetchProperties ?? {})
      .flatMap((fp) => fp.properties?.subGroups ?? [])
      .find((group) => group.name === 'rse');
    if (rseGroup && !connectionProperties['rse']) {
      connectionProperties['rse'] = {
        class: 'io.openlegacy.core.dataclass.DataClassItem.Group',
        fieldName: 'rse',
        type: ConnectorOptionsPropertyType.CLASS,
        required: false,
        description: 'RSE API configuration for design-time source browsing',
        properties: rseGroup.properties,
      };
    }
  }

  testConnectionWithNewProfile(
    connectionId: string,
    profile?: ConnectionProfileParams,
  ): Observable<TestConnectionResult> {
    return this.http.post<TestConnectionResult>(
      `${this.connectionEntryPoint}/${connectionId}/test-new-profile`,
      profile,
    );
  }

  testNewConnection(
    connectorName: string,
    profile?: ConnectionProfileParams,
  ): Observable<TestConnectionResult> {
    return this.http.post<TestConnectionResult>(
      `${this.connectionEntryPoint}/${connectorName}/test-new-connection`,
      profile,
    );
  }

  testConnectionProfile(
    connectionId: string,
    profileName: string,
  ): Observable<TestConnectionResult> {
    const queryParams = new HttpParams({
      fromObject: { profile: profileName },
    });
    return this.http.get<TestConnectionResult>(
      `${this.connectionEntryPoint}/${connectionId}/test-connection`,
      {
        params: queryParams,
      },
    );
  }

  getRuntimeProperties(connectorName: string): Observable<{
    runtimePropertiesKeys: string[];
  }> {
    return this.http.get<{ runtimePropertiesKeys: string[] }>(
      `${this.connectionEntryPoint}/connector/${connectorName}/runtime-properties`,
    );
  }

  /** Get all connection names for validation (e.g. duplicate check before create). */
  names(): Observable<BulkNamesResponse> {
    return this.http.get<BulkNamesResponse>(`${this.connectionEntryPoint}/names`);
  }

  private fillDefaultProfileValue(): UnaryFunction<Observable<Connection>, Observable<Connection>> {
    return pipe(
      map((connection: Connection) => {
        if (connection && !(connection as Connection).profiles) {
          (connection as Connection).profiles = {
            [DEFAULT_CONNECTION_PROFILE_NAME]: {},
          };
        }
        return connection;
      }),
    );
  }

  /**
   * Fetch legops options by type
   * @param type string
   * @returns Observable<{options: ConnectorConfig}>
   */
  fetchLegopsOptions(type: string): Observable<{ options: ConnectorConfig }> {
    return this.http.get<{ options: ConnectorConfig }>(
      `${this.connectionEntryPoint}/legops/${type}/options`,
    );
  }

  /**
   * Tests the new Legops connection with the specified type and optional profile parameters.
   *
   * @param {string} type The type of the connection to test.
   * @param {ConnectionProfileParams} [profile] Optional profile parameters for the connection test.
   * @return {Observable<TestConnectionResult>} An observable that emits the result of the connection test.
   */
  testNewLegopsConnection(
    type: string,
    profile?: ConnectionProfileParams,
  ): Observable<TestConnectionResult> {
    return this.http.post<TestConnectionResult>(
      `${this.connectionEntryPoint}/legops/${type}/test-new-connection`,
      profile,
    );
  }
}
